import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

// --- IMPORTANT: REPLACE THESE WITH YOUR ACTUAL SUPABASE PROJECT DETAILS ---
// You get these from your Supabase Project Settings -> API
const SUPABASE_URL = 'https://xtevsrswajhsfhciomzw.supabase.co'; // e.g., 'https://abcdef12345.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh0ZXZzcnN3YWpoc2ZoY2lvbXp3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE1NTQ2NzAsImV4cCI6MjA2NzEzMDY3MH0.H3Zh8IUklnJgx8-5EogOnMaWmONWt-Wr_8kUj5CgXaE'; // e.g., 'eyJhbGciOiJIUzI1NiJ...'
// --------------------------------------------------------------------------

// Initialize Supabase client
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Helper to calculate age
const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// App Component
function App() {
  const [session, setSession] = useState(null); // New state to hold the Supabase session
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [members, setMembers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState('dashboard'); // Default to dashboard
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [memberFilter, setMemberFilter] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('');
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [showRenewalNotification, setShowRenewalNotification] = useState(true); // New state to control notification visibility


  // Authentication and initial data fetch
  useEffect(() => {
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUserId(session?.user?.id || null);
      setIsAuthReady(true);
      setLoading(false);
    });

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUserId(session?.user?.id || null);
      setIsAuthReady(true);
      setLoading(false);
    });

    return () => {
      authListener?.unsubscribe(); // Clean up auth listener
    };
  }, []);

  // Fetch members and attendance records and set up real-time subscriptions
  useEffect(() => {
    if (!isAuthReady || !userId) return; // Only fetch if authenticated

    const fetchMembers = async () => {
      try {
        const { data, error } = await supabase
          .from('members')
          .select('*')
          .eq('user_id', userId)
          .order('name', { ascending: true }); // Order members by name
        if (error) throw error;

        const fetchedMembers = data.map(member => ({
          ...member,
          dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth) : null,
          dateOfGrading: member.dateOfGrading ? new Date(member.dateOfGrading) : null,
          kkoLicenseRenewalDate: member.kkoLicenseRenewalDate ? new Date(member.kkoLicenseRenewalDate) : null,
          ekaLicenseRenewalDate: member.ekaLicenseRenewalDate ? new Date(member.ekaLicenseRenewalDate) : null,
        }));
        setMembers(fetchedMembers);
      } catch (error) {
        console.error("Error fetching members:", error.message);
        setMessage("Error loading members. Please check your Supabase RLS policies and network connection.");
      }
    };

    const fetchAttendance = async () => {
      try {
        const { data, error } = await supabase
          .from('attendance')
          .select('*')
          .eq('user_id', userId)
          .order('date', { ascending: false }); // Order attendance by date descending
        if (error) throw error;

        const fetchedAttendance = data.map(record => ({
          ...record,
          date: record.date ? new Date(record.date) : null,
        }));
        setAttendanceRecords(fetchedAttendance);
      } catch (error) {
        console.error("Error fetching attendance records:", error.message);
        setMessage("Error loading attendance records. Please check your Supabase RLS policies and network connection.");
      }
    };

    fetchMembers();
    fetchAttendance();

    // Real-time subscriptions for immediate UI updates
    const memberSubscription = supabase
      .channel('public:members')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'members' }, payload => {
        // Only re-fetch if the change is relevant to the current user's data
        if (payload.new?.user_id === userId || payload.old?.user_id === userId) {
          fetchMembers();
        }
      })
      .subscribe();

    const attendanceSubscription = supabase
      .channel('public:attendance')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'attendance' }, payload => {
        if (payload.new?.user_id === userId || payload.old?.user_id === userId) {
          fetchAttendance();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(memberSubscription);
      supabase.removeChannel(attendanceSubscription);
    };
  }, [isAuthReady, userId]); // Re-run when auth state or userId changes

  // Utility to show temporary messages
  const showMessage = useCallback((text, duration = 3000) => {
    setMessage(text);
    setTimeout(() => setMessage(''), duration);
  }, []);

  // License Renewal Notification Logic
  useEffect(() => {
    const today = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(today.getFullYear() + 1);
    const fourWeeksFromNow = new Date();
    fourWeeksFromNow.setDate(today.getDate() + 28);
    const twoWeeksFromNow = new Date(); // New threshold for "very urgent" display
    twoWeeksFromNow.setDate(today.getDate() + 14);

    const renewals = [];

    members.forEach(member => {
      // Check KKO License Renewal
      if (member.kkoLicenseRenewalDate instanceof Date && !isNaN(member.kkoLicenseRenewalDate)) {
        if (member.kkoLicenseRenewalDate > today && member.kkoLicenseRenewalDate <= oneYearFromNow) {
          const isUrgent = member.kkoLicenseRenewalDate <= fourWeeksFromNow;
          const isVeryUrgent = member.kkoLicenseRenewalDate <= twoWeeksFromNow; // New flag
          renewals.push({
            memberId: member.id,
            memberName: member.name,
            type: 'KKO License',
            date: member.kkoLicenseRenewalDate,
            isUrgent: isUrgent,
            isVeryUrgent: isVeryUrgent // Store new flag
          });
        }
      }

      // Check EKA License Renewal
      if (member.ekaLicenseRenewalDate instanceof Date && !isNaN(member.ekaLicenseRenewalDate)) {
        if (member.ekaLicenseRenewalDate > today && member.ekaLicenseRenewalDate <= oneYearFromNow) {
          const isUrgent = member.ekaLicenseRenewalDate <= fourWeeksFromNow;
          const isVeryUrgent = member.ekaLicenseRenewalDate <= twoWeeksFromNow; // New flag
          renewals.push({
            memberId: member.id,
            memberName: member.name,
            type: 'EKA License',
            date: member.ekaLicenseRenewalDate,
            isUrgent: isUrgent,
            isVeryUrgent: isVeryUrgent // Store new flag
          });
        }
      }
    });

    // Sort renewals by date
    renewals.sort((a, b) => a.date - b.date);
    setUpcomingRenewals(renewals); // Store all relevant renewals (up to 12 months)
    setShowRenewalNotification(true); // Reset notification visibility when renewals change
  }, [members]); // Recalculate when members data changes

  // Function to handle editing a member
  const handleEditMember = (member) => {
    setEditingMember(member);
    setCurrentPage('add-member');
  };

  // Function to clear editing state
  const handleCancelEdit = () => {
    setEditingMember(null);
    setCurrentPage('member-details');
  };

  // Function to handle deleting a member
  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member and all their attendance records? This action cannot be undone.")) {
      return;
    }

    try {
      // 1. Delete member's attendance records first
      const { error: attendanceError } = await supabase
        .from('attendance')
        .delete()
        .eq('memberId', memberId)
        .eq('user_id', userId); // Ensure only deleting own records
      if (attendanceError) throw attendanceError;

      // 2. Then delete the member document
      const { error: memberError } = await supabase
        .from('members')
        .delete()
        .eq('id', memberId)
        .eq('user_id', userId); // Ensure only deleting own members
      if (memberError) throw memberError;

      showMessage('Member and all associated attendance records removed successfully!', 3000);
    } catch (error) {
      console.error('Error removing member:', error.message);
      showMessage('Failed to remove member. Please try again.', 3000);
    }
  };

  // Function to download individual member's data and attendance
  const handleDownloadMemberData = (member) => {
    const memberAttendance = attendanceRecords.filter(record => record.memberId === member.id);

    let csvContent = "";

    // Add member details header and data
    csvContent += "Member Details\n";
    csvContent += "Field,Value\n";
    csvContent += `"Name","${member.name || ''}"\n`;
    csvContent += `"Gender","${member.gender || ''}"\n`;
    csvContent += `"Date of Birth","${member.dateOfBirth ? member.dateOfBirth.toLocaleDateString('en-GB') : ''}"\n`;
    csvContent += `"Age","${calculateAge(member.dateOfBirth)}"\n`;
    csvContent += `"Grade","${member.grade || ''}"\n`;
    csvContent += `"Date of Last Grading","${member.dateOfGrading ? member.dateOfGrading.toLocaleDateString('en-GB') : ''}"\n`;
    csvContent += `"Lessons Since Last Grading","${member.lessonsSinceLastGrading || 0}"\n`;
    csvContent += `"KKO License No","${member.kkoLicenseNumber || ''}"\n`;
    csvContent += `"KKO Renewal Date","${member.kkoLicenseRenewalDate ? member.kkoLicenseRenewalDate.toLocaleDateString('en-GB') : ''}"\n`;
    csvContent += `"EKA License No","${member.ekaLicenseNumber || ''}"\n`;
    csvContent += `"EKA Renewal Date","${member.ekaLicenseRenewalDate ? member.ekaLicenseRenewalDate.toLocaleDateString('en-GB') : ''}"\n`;
    csvContent += `"Address Line 1","${member.addressLine1 || ''}"\n`;
    csvContent += `"Address Line 2","${member.addressLine2 || ''}"\n`;
    csvContent += `"City/Town","${member.city || ''}"\n`;
    csvContent += `"Postcode","${member.postcode || ''}"\n`;
    csvContent += `"Emergency Contact Name","${member.emergencyContactName || ''}"\n`;
    csvContent += `"Emergency Contact Phone","${member.emergencyContactPhone || ''}"\n`;

    // Add a separator
    csvContent += "\nAttendance History\n";

    // Add attendance history header
    csvContent += "Date,Status\n";

    // Add attendance history data
    if (memberAttendance.length > 0) {
      // Sort attendance records by date descending for the CSV
      const sortedAttendance = [...memberAttendance].sort((a, b) => b.date - a.date);
      sortedAttendance.forEach(record => {
        csvContent += `"${record.date.toLocaleDateString('en-GB')}","${record.status}"\n`;
      });
    } else {
      csvContent += "No attendance records found for this member.\n";
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${member.name.replace(/\s/g, '_')}_details_and_attendance.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    }
    showMessage(`Downloading data for ${member.name}...`, 2000);
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      showMessage('Signed out successfully!', 3000);
      // Clear local data after sign out
      setMembers([]);
      setAttendanceRecords([]);
      setUpcomingRenewals([]);
      setCurrentPage('dashboard'); // Go back to dashboard or login screen
    } catch (error) {
      console.error('Error signing out:', error.message);
      showMessage('Failed to sign out. Please try again.', 3000);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="text-xl font-semibold text-gray-700">Loading application...</div>
      </div>
    );
  }

  // Render AuthScreen if no session is active
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-700 text-white font-inter p-6 sm:p-8 md:p-12 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto bg-white shadow-2xl rounded-2xl p-8 md:p-12 border border-purple-400">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-700 mb-10 tracking-tight">
            🥋 Rotherham Shukokai Karate Club
          </h1>
          {message && (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-md relative mb-6 text-center text-sm shadow-sm" role="alert">
              {message}
            </div>
          )}
          <AuthScreen supabase={supabase} showMessage={showMessage} />
        </div>
      </div>
    );
  }

  // Filter for renewals due within 2 weeks for the notification display
  const veryUrgentRenewals = upcomingRenewals.filter(renewal => renewal.isVeryUrgent);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-700 text-white font-inter p-6 sm:p-8 md:p-12 flex items-center justify-center">
      <div className="max-w-4xl w-full mx-auto bg-white shadow-2xl rounded-2xl p-8 md:p-12 border border-purple-400 transform transition-all duration-300 hover:shadow-3xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-700 mb-10 tracking-tight">
          🥋 Rotherham Shukokai Karate Club Attendance
        </h1>

        {userId && (
          <p className="text-xs text-center text-gray-500 mb-8 px-2">
            Your User ID: <span className="font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded-md break-all">{userId}</span>
          </p>
        )}

        {/* Global Message/Notification Area */}
        {message && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-md relative mb-6 text-center text-sm shadow-sm" role="alert">
            {message}
          </div>
        )}

        {/* Concise License Renewal Notification Area */}
        {showRenewalNotification && veryUrgentRenewals.length > 0 && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded-md relative mb-6 text-center text-sm shadow-sm flex items-center justify-between">
            <p className="font-semibold flex-grow">
              ⚠️ {veryUrgentRenewals.length} license(s) due in &lt; 2 weeks!
            </p>
            <button
              onClick={() => setShowRenewalNotification(false)} // Dismiss this notification
              className="ml-4 text-yellow-700 hover:text-yellow-900 focus:outline-none"
              aria-label="Dismiss renewal notifications"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          </div>
        )}


        <nav className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 mb-10">
          <Button
            onClick={() => setCurrentPage('dashboard')}
            isActive={currentPage === 'dashboard'}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2 2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
            }
          >
            Dashboard
          </Button>
          <Button
            onClick={() => { setCurrentPage('add-member'); setEditingMember(null); }}
            isActive={currentPage === 'add-member' && !editingMember}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
            }
          >
            Add Member
          </Button>
          <Button
            onClick={() => setCurrentPage('record-attendance')}
            isActive={currentPage === 'record-attendance'}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
            }
          >
            Record Attendance
          </Button>
          <Button
            onClick={() => setCurrentPage('view-history')}
            isActive={currentPage === 'view-history'}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10h2m-2 0h2m2 0h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2m-2 8h2m-2 0h2m-2 0h2"></path></svg>
            }
          >
            View History
          </Button>
          <Button
            onClick={() => { setCurrentPage('member-details'); setEditingMember(null); }}
            isActive={currentPage === 'member-details' && !editingMember}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0h4m-4 0H7m6 0h-2M6 18h9"></path></svg>
            }
          >
            Member Details
          </Button>
          <Button
            onClick={handleSignOut}
            isActive={false}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H5a3 3 0 01-3-3v-10a3 3 0 013-3h5a3 3 0 013 3v1"></path></svg>
            }
          >
            Sign Out
          </Button>
        </nav>

        {isAuthReady && userId ? (
          <>
            {currentPage === 'dashboard' && (
              <Dashboard
                members={members}
                attendanceRecords={attendanceRecords}
              />
            )}
            {currentPage === 'add-member' && (
              <AddMemberForm
                userId={userId}
                supabase={supabase}
                showMessage={showMessage}
                editingMember={editingMember}
                onSave={handleCancelEdit}
                onCancel={handleCancelEdit}
              />
            )}
            {currentPage === 'record-attendance' && (
              <AttendanceRecorder
                members={members}
                userId={userId}
                supabase={supabase}
                attendanceRecords={attendanceRecords}
                showMessage={showMessage}
              />
            )}
            {currentPage === 'view-history' && (
              <AttendanceHistory
                members={members}
                attendanceRecords={attendanceRecords}
                attendanceFilter={attendanceFilter}
                setAttendanceFilter={setAttendanceFilter}
              />
            )}
            {currentPage === 'member-details' && (
              <MemberDetailsViewer
                members={members}
                attendanceRecords={attendanceRecords}
                onEditMember={handleEditMember}
                onDeleteMember={handleDeleteMember}
                onDownloadMemberData={handleDownloadMemberData}
                memberFilter={memberFilter}
                setMemberFilter={setMemberFilter}
              />
            )}
          </>
        ) : (
          <div className="text-center text-gray-700 text-lg py-10">
            Authenticating... Please wait.
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Button Component
const Button = ({ onClick, isActive, icon, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center justify-center w-full px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-300 ease-in-out transform
      ${isActive
        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-xl scale-105 border-2 border-purple-300'
        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 hover:text-gray-800 hover:shadow-md hover:scale-102'
      } focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 active:scale-98`}
  >
    {icon && <span className="mr-2">{icon}</span>}
    {children}
  </button>
);

// AuthScreen Component for Sign Up/Sign In
function AuthScreen({ supabase, showMessage }) {
  const [isSignIn, setIsSignIn] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignIn) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        showMessage('Signed in successfully!', 3000);
      } else {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        showMessage('Sign up successful! Please check your email for a confirmation link.', 5000);
      }
    } catch (error) {
      console.error('Authentication error:', error.message);
      showMessage(`Authentication failed: ${error.message}`, 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
        {isSignIn ? 'Sign In' : 'Sign Up'}
      </h2>
      <form onSubmit={handleAuth} className="w-full space-y-6">
        <div>
          <label htmlFor="email" className="block text-gray-700 text-sm font-semibold mb-2">Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-gray-700 text-sm font-semibold mb-2">Password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-102 active:scale-98 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : (isSignIn ? 'Sign In' : 'Sign Up')}
        </button>
      </form>
      <button
        onClick={() => setIsSignIn(!isSignIn)}
        className="mt-6 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200 focus:outline-none"
      >
        {isSignIn ? 'Need an account? Sign Up' : 'Already have an account? Sign In'}
      </button>
    </div>
  );
}


// Add/Edit Member Form Component
function AddMemberForm({ userId, supabase, showMessage, editingMember, onSave, onCancel }) {
  const [memberName, setMemberName] = useState(editingMember ? editingMember.name : '');
  const [emergencyContactName, setEmergencyContactName] = useState(editingMember ? editingMember.emergencyContactName : '');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState(editingMember ? editingMember.emergencyContactPhone : '');
  const [grade, setGrade] = useState(editingMember ? editingMember.grade : '');
  const [dateOfGrading, setDateOfGrading] = useState(editingMember && editingMember.dateOfGrading ? editingMember.dateOfGrading.toISOString().split('T')[0] : '');
  const [lessonsSinceLastGrading, setLessonsSinceLastGrading] = useState(editingMember ? editingMember.lessonsSinceLastGrading : '');
  const [kkoLicenseNumber, setKkoLicenseNumber] = useState(editingMember ? editingMember.kkoLicenseNumber : '');
  const [kkoLicenseRenewalDate, setKkoLicenseRenewalDate] = useState(editingMember && editingMember.kkoLicenseRenewalDate ? editingMember.kkoLicenseRenewalDate.toISOString().split('T')[0] : '');
  const [ekaLicenseNumber, setEkaLicenseNumber] = useState(editingMember ? editingMember.ekaLicenseNumber : '');
  const [ekaLicenseRenewalDate, setEkaLicenseRenewalDate] = useState(editingMember && editingMember.ekaLicenseRenewalDate ? editingMember.ekaLicenseRenewalDate.toISOString().split('T')[0] : '');
  const [dateOfBirth, setDateOfBirth] = useState(editingMember && editingMember.dateOfBirth ? editingMember.dateOfBirth.toISOString().split('T')[0] : '');
  const [addressLine1, setAddressLine1] = useState(editingMember ? editingMember.addressLine1 : '');
  const [addressLine2, setAddressLine2] = useState(editingMember ? editingMember.addressLine2 : '');
  const [city, setCity] = useState(editingMember ? editingMember.city : '');
  const [postcode, setPostcode] = useState(editingMember ? editingMember.postcode : '');
  const [gender, setGender] = useState(editingMember ? editingMember.gender : '');

  // Update form fields if editingMember changes
  useEffect(() => {
    if (editingMember) {
      setMemberName(editingMember.name);
      setEmergencyContactName(editingMember.emergencyContactName || '');
      setEmergencyContactPhone(editingMember.emergencyContactPhone || '');
      setGrade(editingMember.grade || '');
      setDateOfGrading(editingMember.dateOfGrading ? editingMember.dateOfGrading.toISOString().split('T')[0] : '');
      setLessonsSinceLastGrading(editingMember.lessonsSinceLastGrading || 0);
      setKkoLicenseNumber(editingMember.kkoLicenseNumber || '');
      setKkoLicenseRenewalDate(editingMember.kkoLicenseRenewalDate ? editingMember.kkoLicenseRenewalDate.toISOString().split('T')[0] : '');
      setEkaLicenseNumber(editingMember.ekaLicenseNumber || '');
      setEkaLicenseRenewalDate(editingMember.ekaLicenseRenewalDate ? editingMember.ekaLicenseRenewalDate.toISOString().split('T')[0] : '');
      setDateOfBirth(editingMember.dateOfBirth ? editingMember.dateOfBirth.toISOString().split('T')[0] : '');
      setAddressLine1(editingMember.addressLine1 || '');
      setAddressLine2(editingMember.addressLine2 || '');
      setCity(editingMember.city || '');
      setPostcode(editingMember.postcode || '');
      setGender(editingMember.gender || '');
    } else {
      // Reset fields when not editing
      setMemberName('');
      setEmergencyContactName('');
      setEmergencyContactPhone('');
      setGrade('');
      setDateOfGrading('');
      setLessonsSinceLastGrading('');
      setKkoLicenseNumber('');
      setKkoLicenseRenewalDate('');
      setEkaLicenseNumber('');
      setEkaLicenseRenewalDate('');
      setDateOfBirth('');
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setPostcode('');
      setGender('');
    }
  }, [editingMember]);

  // Define the grade options
  const gradeOptions = [
    '', // Empty option for placeholder
    '9 Kyu', '8 Kyu', '7 Kyu', '6 Kyu', '5 Kyu', '4 Kyu', '3 Kyu', '2 Kyu', '1 Kyu',
    '1 Dan', '2 Dan', '3 Dan', '4 Dan', '5 Dan', '6 Dan', '7 Dan'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberName.trim()) {
      showMessage('Member name cannot be empty.', 2000);
      return;
    }

    const memberData = {
      name: memberName.trim(),
      emergencyContactName: emergencyContactName.trim(),
      emergencyContactPhone: emergencyContactPhone.trim(),
      grade: grade.trim(),
      dateOfGrading: dateOfGrading || null,
      lessonsSinceLastGrading: lessonsSinceLastGrading ? parseInt(lessonsSinceLastGrading) : 0,
      kkoLicenseNumber: kkoLicenseNumber.trim(),
      kkoLicenseRenewalDate: kkoLicenseRenewalDate || null,
      ekaLicenseNumber: ekaLicenseNumber.trim(),
      ekaLicenseRenewalDate: ekaLicenseRenewalDate || null,
      dateOfBirth: dateOfBirth || null,
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim(),
      city: city.trim(),
      postcode: postcode.trim(),
      gender: gender,
      user_id: userId,
    };

    try {
      if (editingMember) {
        const { error } = await supabase
          .from('members')
          .update(memberData)
          .eq('id', editingMember.id)
          .eq('user_id', userId);
        if (error) throw error;
        showMessage('Member updated successfully!', 3000);
      } else {
        const { error } = await supabase
          .from('members')
          .insert(memberData);
        if (error) throw error;
        showMessage('Member added successfully!', 3000);
      }
      onSave();
    } catch (error) {
      console.error(`Error ${editingMember ? 'updating' : 'adding'} member:`, error.message);
      showMessage(`Failed to ${editingMember ? 'update' : 'add'} member. Please try again.`, 3000);
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl border border-gray-200 text-gray-800 shadow-lg">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">
        {editingMember ? 'Edit Member Details' : 'Add New Member'}
      </h2>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-6">
        <div className="md:col-span-2">
          <label htmlFor="member-name" className="block text-gray-700 text-sm font-semibold mb-3">Member Name:</label>
          <input
            type="text"
            id="member-name"
            value={memberName}
            onChange={(e) => setMemberName(e.target.value)}
            placeholder="Enter member name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Member name"
            required
          />
        </div>

        <div>
          <label htmlFor="date-of-birth" className="block text-gray-700 text-sm font-semibold mb-3">Date of Birth (Optional):</label>
          <input
            type="date"
            id="date-of-birth"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Date of Birth"
          />
        </div>

        {/* Gender Radio Buttons */}
        <div className="flex flex-col">
          <span className="block text-gray-700 text-sm font-semibold mb-3">Gender (Optional):</span>
          <div className="flex gap-4 p-3 border border-gray-300 rounded-lg bg-gray-50">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-blue-600 h-5 w-5 border-gray-300 focus:ring-blue-500 transition-all duration-200"
                name="gender"
                value="Male"
                checked={gender === 'Male'}
                onChange={(e) => setGender(e.target.value)}
              />
              <span className="ml-2 text-gray-700">Male</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio text-blue-600 h-5 w-5 border-gray-300 focus:ring-blue-500 transition-all duration-200"
                name="gender"
                value="Female"
                checked={gender === 'Female'}
                onChange={(e) => setGender(e.target.value)}
              />
              <span className="ml-2 text-gray-700">Female</span>
            </label>
          </div>
        </div>

        <div>
          <label htmlFor="member-grade" className="block text-gray-700 text-sm font-semibold mb-3">Grade:</label>
          <select
            id="member-grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 transition-all duration-200"
            aria-label="Member grade"
          >
            <option value="">Select Grade (Optional)</option>
            {gradeOptions.slice(1).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="date-of-grading" className="block text-gray-700 text-sm font-semibold mb-3">Date of Last Grading (Optional):</label>
          <input
            type="date"
            id="date-of-grading"
            value={dateOfGrading}
            onChange={(e) => setDateOfGrading(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Date of last grading"
          />
        </div>

        <div>
          <label htmlFor="initial-lessons" className="block text-gray-700 text-sm font-semibold mb-3">Initial Lessons Since Last Grading (Optional):</label>
          <input
            type="number"
            id="initial-lessons"
            value={lessonsSinceLastGrading}
            onChange={(e) => setLessonsSinceLastGrading(e.target.value)}
            placeholder="e.g., 10"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Initial number of lessons since last grading"
            min="0"
          />
        </div>

        <div>
          <label htmlFor="kko-license-number" className="block text-gray-700 text-sm font-semibold mb-3">KKO License Number (Optional):</label>
          <input
            type="text"
            id="kko-license-number"
            value={kkoLicenseNumber}
            onChange={(e) => setKkoLicenseNumber(e.target.value)}
            placeholder="e.g., KKO12345"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="KKO License Number"
          />
        </div>

        <div>
          <label htmlFor="kko-renewal-date" className="block text-gray-700 text-sm font-semibold mb-3">KKO License Renewal Date (Optional):</label>
          <input
            type="date"
            id="kko-renewal-date"
            value={kkoLicenseRenewalDate}
            onChange={(e) => setKkoLicenseRenewalDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="KKO License Renewal Date"
          />
        </div>

        <div>
          <label htmlFor="eka-license-number" className="block text-gray-700 text-sm font-semibold mb-3">EKA License Number (Optional):</label>
          <input
            type="text"
            id="eka-license-number"
            value={ekaLicenseNumber}
            onChange={(e) => setEkaLicenseNumber(e.target.value)}
            placeholder="e.g., EKA67890"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="EKA License Number"
          />
        </div>

        <div>
          <label htmlFor="eka-renewal-date" className="block text-gray-700 text-sm font-semibold mb-3">EKA License Renewal Date (Optional):</label>
          <input
            type="date"
            id="eka-renewal-date"
            value={ekaLicenseRenewalDate}
            onChange={(e) => setEkaLicenseRenewalDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="EKA License Renewal Date"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="address-line1" className="block text-gray-700 text-sm font-semibold mb-3">Address Line 1 (Optional):</label>
          <input
            type="text"
            id="address-line1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="e.g., 123 Main St"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Address Line 1"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="address-line2" className="block text-gray-700 text-sm font-semibold mb-3">Address Line 2 (Optional):</label>
          <input
            type="text"
            id="address-line2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="e.g., Apt 4B"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Address Line 2"
          />
        </div>

        <div>
          <label htmlFor="city" className="block text-gray-700 text-sm font-semibold mb-3">City/Town (Optional):</label>
          <input
            type="text"
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g., Rotherham"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="City/Town"
          />
        </div>

        <div>
          <label htmlFor="postcode" className="block text-gray-700 text-sm font-semibold mb-3">Postcode (Optional):</label>
          <input
            type="text"
            id="postcode"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="e.g., S60 1AA"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Postcode"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="emergency-contact-name" className="block text-gray-700 text-sm font-semibold mb-3">Emergency Contact Name (Optional):</label>
          <input
            type="text"
            id="emergency-contact-name"
            value={emergencyContactName}
            onChange={(e) => setEmergencyContactName(e.target.value)}
            placeholder="e.g., Jane Doe"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Emergency contact name"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="emergency-contact-phone" className="block text-gray-700 text-sm font-semibold mb-3">Emergency Contact Phone (Optional):</label>
          <input
            type="tel"
            id="emergency-contact-phone"
            value={emergencyContactPhone}
            onChange={(e) => setEmergencyContactPhone(e.target.value)}
            placeholder="e.g., +44 7123 456789"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
            aria-label="Emergency contact phone"
          />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 mt-4">
          <button
            type="submit"
            className="flex-grow bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-102 active:scale-98 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
          >
            {editingMember ? 'Save Changes' : 'Add Member'}
          </button>
          {editingMember && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-grow bg-gray-400 hover:bg-gray-500 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-102 active:scale-98 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// Attendance Recorder Component
function AttendanceRecorder({ members, userId, supabase, attendanceRecords, showMessage }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); //YYYY-MM-DD
  const [attendanceStatus, setAttendanceStatus] = useState({});

  // Populate attendanceStatus when members or selectedDate changes
  useEffect(() => {
    if (!members.length) return;

    const newStatus = {};
    const dateFilter = (record) => {
        const recordDate = record.date.toISOString().split('T')[0];
        return recordDate === selectedDate;
    };

    members.forEach(member => {
        const existingRecord = attendanceRecords.find(record =>
            record.memberId === member.id && dateFilter(record)
        );
        newStatus[member.id] = existingRecord ? existingRecord.status : 'Absent'; // Default to Absent
    });
    setAttendanceStatus(newStatus);
  }, [members, selectedDate, attendanceRecords]);


  const handleStatusChange = (memberId, status) => {
    setAttendanceStatus(prev => ({ ...prev, [memberId]: status }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedDate) {
      showMessage('Please select a date.', 2000);
      return;
    }

    try {
      const memberUpdates = {}; // To store member updates for batching
      const dateToSave = selectedDate; // Supabase date type expects 'YYYY-MM-DD' string

      for (const memberId in attendanceStatus) {
        const newStatus = attendanceStatus[memberId];
        const existingRecord = attendanceRecords.find(record =>
          record.memberId === memberId && record.date.toISOString().split('T')[0] === selectedDate
        );
        const oldStatus = existingRecord ? existingRecord.status : null; // null if new record

        // Determine if lessons count needs adjustment
        let lessonsAdjustment = 0;
        if (newStatus === 'Present' && oldStatus !== 'Present') {
            lessonsAdjustment = 1; // Was Absent/null, now Present -> increment
        } else if (newStatus === 'Absent' && oldStatus === 'Present') {
            lessonsAdjustment = -1; // Was Present, now Absent -> decrement
        }

        // Update attendance record (insert or update)
        if (existingRecord) {
          const { error } = await supabase
            .from('attendance')
            .update({ status: newStatus })
            .eq('id', existingRecord.id)
            .eq('user_id', userId);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('attendance')
            .insert({
              memberId: memberId,
              date: dateToSave,
              status: newStatus,
              user_id: userId,
            });
          if (error) throw error;
        }

        // Apply lessons adjustment if needed
        if (lessonsAdjustment !== 0) {
            const member = members.find(m => m.id === memberId);
            if (member) {
                const currentLessons = member.lessonsSinceLastGrading || 0;
                // Accumulate the adjustment for this member
                memberUpdates[memberId] = (memberUpdates[memberId] || currentLessons) + lessonsAdjustment;
            }
        }
      }

      // Execute member profile updates for lessonsSinceLastGrading
      const memberUpdatePromises = Object.entries(memberUpdates).map(async ([memberId, newLessonsCount]) => {
        const { error } = await supabase
          .from('members')
          .update({ lessonsSinceLastGrading: newLessonsCount })
          .eq('id', memberId)
          .eq('user_id', userId);
        if (error) throw error;
      });
      await Promise.all(memberUpdatePromises);

      showMessage('Attendance saved successfully!', 3000);
    } catch (error) {
      console.error('Error saving attendance:', error.message);
      showMessage('Failed to save attendance. Please try again.', 3000);
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl border border-gray-200 text-gray-800 shadow-lg">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Record Attendance</h2>

      <div className="mb-8 flex flex-col sm:flex-row items-center gap-4">
        <label htmlFor="attendance-date" className="font-semibold text-lg text-gray-700">Date:</label>
        <input
          type="date"
          id="attendance-date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 transition-all duration-200"
          aria-label="Select attendance date"
        />
      </div>

      {members.length === 0 ? (
        <p className="text-center text-gray-600 italic text-lg">No members added yet. Please go to 'Add Member' tab.</p>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <div key={member.id} className="flex flex-col sm:flex-row items-center justify-between bg-gray-100 p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
              <span className="font-semibold text-lg text-gray-800 mb-2 sm:mb-0">{member.name}</span>
              <div className="flex gap-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    className="form-radio text-green-600 h-5 w-5 border-gray-300 focus:ring-green-500 transition-all duration-200"
                    name={`status-${member.id}`}
                    value="Present"
                    checked={attendanceStatus[member.id] === 'Present'}
                    onChange={() => handleStatusChange(member.id, 'Present')}
                  />
                  <span className="ml-2 text-green-700 font-bold">Present</span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="radio"
                    className="form-radio text-red-600 h-5 w-5 border-gray-300 focus:ring-red-500 transition-all duration-200"
                    name={`status-${member.id}`}
                    value="Absent"
                    checked={attendanceStatus[member.id] === 'Absent'}
                    onChange={() => handleStatusChange(member.id, 'Absent')}
                  />
                  <span className="ml-2 text-red-700 font-bold">Absent</span>
                </label>
              </div>
            </div>
          ))}
          <button
            onClick={handleSaveAttendance}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-102 active:scale-98 mt-6 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          >
            Save Attendance
          </button>
        </div>
      )}
    </div>
  );
}

// Attendance History Component
function AttendanceHistory({ members, attendanceRecords, attendanceFilter, setAttendanceFilter }) {
  // Create a map for quick member lookup
  const memberMap = members.reduce((acc, member) => {
    acc[member.id] = member.name;
    return acc;
  }, {});

  // Filter attendance records based on the attendanceFilter
  const filteredAttendanceRecords = attendanceRecords.filter(record => {
    const memberName = memberMap[record.memberId] || 'Unknown Member';
    return memberName.toLowerCase().includes(attendanceFilter.toLowerCase());
  });

  // Group filtered attendance records by date
  const attendanceByDate = filteredAttendanceRecords.reduce((acc, record) => {
    const dateKey = record.date.toISOString().split('T')[0]; //YYYY-MM-DD
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(record);
    return acc;
  }, {});

  // Sort dates in descending order
  const sortedDates = Object.keys(attendanceByDate).sort((a, b) => new Date(b) - new Date(a));

  // Function to download attendance as CSV
  const downloadCsv = () => {
    const headers = ["Date", "Member Name", "Status"];
    const rows = [];

    // Flatten filtered attendance records and sort them for CSV
    const allRecords = [...filteredAttendanceRecords].sort((a, b) => {
        // Sort by date (descending) then by member name (ascending)
        const dateComparison = b.date.getTime() - a.date.getTime();
        if (dateComparison !== 0) return dateComparison;
        const nameA = memberMap[a.memberId] || '';
        const nameB = memberMap[b.memberId] || '';
        return nameA.localeCompare(nameB);
    });


    allRecords.forEach(record => {
      const date = record.date.toLocaleDateString('en-GB'); // Format date for readability
      const memberName = memberMap[record.memberId] || 'Unknown Member';
      const status = record.status;
      rows.push([date, memberName, status].map(item => `"${item.replace(/"/g, '""')}"`).join(',')); // Enclose in quotes and escape
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection for download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'karate_attendance_full_history.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up
    } else {
      // Fallback for browsers that don't support download attribute (e.g., some older IE)
      // This is less robust but provides some functionality
      window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl border border-gray-200 text-gray-800 shadow-lg">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Attendance History</h2>

      <div className="mb-6">
        <label htmlFor="attendance-search" className="block text-gray-700 text-sm font-semibold mb-2">Filter by Member Name:</label>
        <input
          type="text"
          id="attendance-search"
          value={attendanceFilter}
          onChange={(e) => setAttendanceFilter(e.target.value)}
          placeholder="Search member name..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
          aria-label="Filter attendance by member name"
        />
      </div>

      {sortedDates.length === 0 ? (
        <p className="text-center text-gray-600 italic text-lg mt-8">No attendance records found for this filter.</p>
      ) : (
        <>
          <button
            onClick={downloadCsv}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-102 active:scale-98 mb-8 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
          >
            <svg className="w-6 h-6 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
            Download Filtered Attendance History (CSV)
          </button>

          <div className="space-y-6">
            {sortedDates.map((dateKey) => (
              <div key={dateKey} className="border border-gray-200 rounded-lg p-5 bg-gray-50 shadow-md hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-300 pb-2">
                  {new Date(dateKey).toLocaleDateString('en-GB', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {attendanceByDate[dateKey]
                    .sort((a, b) => (memberMap[a.memberId] || '').localeCompare(memberMap[b.memberId] || '')) // Sort by member name
                    .map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 bg-white rounded-md shadow-sm border border-gray-100">
                      <span className="font-medium text-lg text-gray-800">{memberMap[record.memberId] || 'Unknown Member'}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        record.status === 'Present' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {record.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Member Details Viewer Component
function MemberDetailsViewer({ members, attendanceRecords, onEditMember, onDeleteMember, onDownloadMemberData, memberFilter, setMemberFilter }) {

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(memberFilter.toLowerCase())
  );

  return (
    <div className="bg-white p-8 rounded-xl border border-gray-200 text-gray-800 shadow-lg">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Member Details</h2>

      <div className="mb-6">
        <label htmlFor="member-search" className="block text-gray-700 text-sm font-semibold mb-2">Filter by Member Name:</label>
        <input
          type="text"
          id="member-search"
          value={memberFilter}
          onChange={(e) => setMemberFilter(e.target.value)}
          placeholder="Search member name..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-500 transition-all duration-200"
          aria-label="Filter members by name"
        />
      </div>

      {filteredMembers.length === 0 ? (
        <p className="text-center text-gray-600 italic text-lg mt-8">No members found for this filter.</p>
      ) : (
        <div className="space-y-6">
          {filteredMembers.map((member) => (
            <div key={member.id} className="bg-gray-50 p-6 rounded-lg border border-gray-100 shadow-md hover:shadow-lg transition-shadow duration-200">
              <h3 className="font-bold text-xl text-gray-900 mb-4">{member.name}</h3>

              {/* Personal Details Group */}
              <div className="bg-gray-100 p-4 rounded-md mb-4 border border-gray-200">
                <h4 className="font-semibold text-md text-gray-700 mb-3">Personal Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-base">
                  <p className="text-gray-700"><span className="font-medium">Gender:</span> {member.gender || 'N/A'}</p>
                  <p className="text-gray-700">
                    <span className="font-medium">Date of Birth:</span> {member.dateOfBirth ? member.dateOfBirth.toLocaleDateString('en-GB') : 'N/A'}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Age:</span> {calculateAge(member.dateOfBirth)}
                  </p>
                  <p className="text-gray-700"><span className="font-medium">Grade:</span> {member.grade || 'N/A'}</p>
                  <p className="text-gray-700">
                    <span className="font-medium">Last Grading:</span> {member.dateOfGrading ? member.dateOfGrading.toLocaleDateString('en-GB') : 'N/A'}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Lessons Since Last Grading:</span> {typeof member.lessonsSinceLastGrading === 'number' ? member.lessonsSinceLastGrading : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Address Details Group */}
              {(member.addressLine1 || member.addressLine2 || member.city || member.postcode) && (
                <div className="bg-gray-100 p-4 rounded-md mb-4 border border-gray-200">
                  <h4 className="font-semibold text-md text-gray-700 mb-3">Address</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-base">
                    <p className="text-gray-700"><span className="font-medium">Address Line 1:</span> {member.addressLine1 || 'N/A'}</p>
                    <p className="text-gray-700"><span className="font-medium">Address Line 2:</span> {member.addressLine2 || 'N/A'}</p>
                    <p className="text-gray-700"><span className="font-medium">City/Town:</span> {member.city || 'N/A'}</p>
                    <p className="text-gray-700"><span className="font-semibold">Postcode:</span> {member.postcode || 'N/A'}</p>
                  </div>
                </div>
              )}

              {/* Emergency Contact Group */}
              {(member.emergencyContactName || member.emergencyContactPhone) && (
                <div className="bg-gray-100 p-4 rounded-md mb-4 border border-gray-200">
                  <h4 className="font-semibold text-md text-gray-700 mb-3">Emergency Contact</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-base">
                    <p className="text-gray-700"><span className="font-medium">Name:</span> {member.emergencyContactName || 'N/A'}</p>
                    <p className="text-gray-700"><span className="font-medium">Phone:</span> {member.emergencyContactPhone || 'N/A'}</p>
                  </div>
                </div>
              )}

              {/* License Details Group */}
              {(member.kkoLicenseNumber || member.kkoLicenseRenewalDate || member.ekaLicenseNumber || member.ekaLicenseRenewalDate) && (
                <div className="bg-gray-100 p-4 rounded-md mb-6 border border-gray-200">
                  <h4 className="font-semibold text-md text-gray-700 mb-3">License Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-base">
                    <p className="text-gray-700"><span className="font-medium">KKO License No:</span> {member.kkoLicenseNumber || 'N/A'}</p>
                    <p className="text-gray-700">
                      <span className="font-medium">KKO Renewal:</span> {member.kkoLicenseRenewalDate ? member.kkoLicenseRenewalDate.toLocaleDateString('en-GB') : 'N/A'}
                    </p>
                    <p className="text-gray-700"><span className="font-medium">EKA License No:</span> {member.ekaLicenseNumber || 'N/A'}</p>
                    <p className="text-gray-700">
                      <span className="font-medium">EKA Renewal:</span> {member.ekaLicenseRenewalDate ? member.ekaLicenseRenewalDate.toLocaleDateString('en-GB') : 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-4 justify-end">
                <button
                  onClick={() => onEditMember(member)}
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-98 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
                >
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                  Edit
                </button>
                <button
                  onClick={() => onDeleteMember(member.id)}
                  className="flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-98 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75"
                >
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                  Remove
                </button>
                <button
                  onClick={() => onDownloadMemberData(member)}
                  className="flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-md transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-98 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
                >
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                  Download Data
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard Component
function Dashboard({ members, attendanceRecords }) {
  // Create a map for quick member lookup
  const memberMap = members.reduce((acc, member) => {
    acc[member.id] = member.name;
    return acc;
  }, {});

  // Calculate total members
  const totalMembers = members.length;

  // Calculate active members (attended in the last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const activeMemberIds = new Set();
  attendanceRecords.forEach(record => {
    if (record.status === 'Present' && record.date >= thirtyDaysAgo) {
      activeMemberIds.add(record.memberId);
    }
  });
  const activeMembersCount = activeMemberIds.size;

  // Calculate attendance trends for last 7 days and last 30 days
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day

  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const thirtyDaysAgoForTrend = new Date(today);
  thirtyDaysAgoForTrend.setDate(today.getDate() - 30);

  let attendanceLast7Days = 0;
  let attendanceLast30Days = 0;

  attendanceRecords.forEach(record => {
    if (record.status === 'Present' && record.date >= sevenDaysAgo) {
      attendanceLast7Days++;
    }
    if (record.status === 'Present' && record.date >= thirtyDaysAgoForTrend) {
      attendanceLast30Days++;
    }
  });

  // Calculate grade distribution
  const gradeDistribution = members.reduce((acc, member) => {
    const grade = member.grade || 'Un-graded';
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  // Sort grades for display
  const sortedGrades = Object.keys(gradeDistribution).sort((a, b) => {
    // Custom sort for grades (e.g., Kyu before Dan, then numerical)
    const gradeOrder = ['Un-graded', '9 Kyu', '8 Kyu', '7 Kyu', '6 Kyu', '5 Kyu', '4 Kyu', '3 Kyu', '2 Kyu', '1 Kyu', '1 Dan', '2 Dan', '3 Dan', '4 Dan', '5 Dan', '6 Dan', '7 Dan'];
    const indexA = gradeOrder.indexOf(a);
    const indexB = gradeOrder.indexOf(b);
    if (indexA === -1 || indexB === -1) { // Handle grades not in our defined order
      return a.localeCompare(b);
    }
    return indexA - indexB;
  });

  return (
    <div className="bg-white p-8 rounded-xl border border-gray-200 text-gray-800 shadow-lg">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Dashboard Overview</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Total Members Card */}
        <div className="bg-blue-100 p-6 rounded-lg shadow-md border border-blue-200 flex items-center justify-between">
          <div>
            <p className="text-blue-800 text-lg font-semibold">Total Members</p>
            <p className="text-blue-900 text-4xl font-bold">{totalMembers}</p>
          </div>
          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H2v-2a3 3 0 015.356-1.857M17 20v-9a2 2 0 00-2-2H9a2 2 0 00-2 2v9m-2-7h10a2 2 0 012 2v7H2v-7a2 2 0 012-2z"></path></svg>
        </div>

        {/* Active Members Card */}
        <div className="bg-green-100 p-6 rounded-lg shadow-md border border-green-200 flex items-center justify-between">
          <div>
            <p className="text-green-800 text-lg font-semibold">Active Members (Last 30 Days)</p>
            <p className="text-green-900 text-4xl font-bold">{activeMembersCount}</p>
          </div>
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.001 12.001 0 002.92 12c0 3.072 1.47 5.898 3.818 7.727A12.004 12.004 0 0012 21c2.348 0 4.507-.753 6.26-2.043A12.006 12.006 0 0021.08 12c0-3.072-1.47-5.898-3.818-7.727z"></path></svg>
        </div>

        {/* Attendance Last 7 Days Card */}
        <div className="bg-purple-100 p-6 rounded-lg shadow-md border border-purple-200 flex items-center justify-between">
          <div>
            <p className="text-purple-800 text-lg font-semibold">Attendance (Last 7 Days)</p>
            <p className="text-purple-900 text-4xl font-bold">{attendanceLast7Days}</p>
          </div>
          <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zm3-7h.01M12 15h.01"></path></svg>
        </div>

        {/* Attendance Last 30 Days Card */}
        <div className="bg-indigo-100 p-6 rounded-lg shadow-md border border-indigo-200 flex items-center justify-between">
          <div>
            <p className="text-indigo-800 text-lg font-semibold">Attendance (Last 30 Days)</p>
            <p className="text-indigo-900 text-4xl font-bold">{attendanceLast30Days}</p>
          </div>
          <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </div>
      </div>

      {/* Grade Distribution */}
      <div className="bg-gray-100 p-6 rounded-lg shadow-md border border-gray-200">
        <h3 className="text-2xl font-bold mb-6 text-gray-800 text-center">Grade Distribution</h3>
        {Object.keys(gradeDistribution).length === 0 ? (
          <p className="text-center text-gray-600 italic">No members with grades to display.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedGrades.map(grade => (
              <div key={grade} className="flex items-center justify-between bg-white p-4 rounded-md shadow-sm border border-gray-100">
                <span className="font-medium text-gray-700">{grade}:</span>
                <span className="font-bold text-gray-900 text-xl">{gradeDistribution[grade]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
